import * as Sentry from "@sentry/react";

const env = import.meta.env as Record<string, string | undefined>;
const dsn = env.VITE_SENTRY_DSN;

/**
 * Starts the browser Sentry client. Takes the router so navigations become transactions. Session
 * Replay, the largest part of the SDK, is added once the page is idle (`addReplayWhenIdle`), so it
 * stays out of the first download; an error replay then misses only the page's first moments.
 */
export function initSentry(
  router: Parameters<typeof Sentry.tanstackRouterBrowserTracingIntegration>[0],
) {
  Sentry.init({
    dsn,
    environment: env.VITE_SENTRY_ENVIRONMENT,
    release: env.VITE_SENTRY_RELEASE,
    // No DSN means no client at all, which is the local and no-Sentry-stage case.
    enabled: Boolean(dsn),
    enableLogs: true,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    tracesSampleRate: 0.1,
    // Read by Replay when it is added after load.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // The SPA calls only the same-origin BFF; the leading (?!\/) keeps out //other-host paths.
    tracePropagationTargets: [/^\/(?!\/)/],
    // Termly's own runtime errors are Termly's, not the storefront's.
    denyUrls: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari(-web)?-extension:\/\//,
      /^https:\/\/app\.termly\.io\//,
    ],
    ignoreErrors: [
      "Load failed",
      "Failed to fetch",
      "NetworkError when attempting to fetch resource.",
    ],
  });
  if (dsn) addReplayWhenIdle();
}

/**
 * Loads Session Replay as a chunk of its own once the page is idle, and adds it to the running
 * client. `@sentry/replay` is the package `@sentry/browser` re-exports `replayIntegration` from; it
 * is listed on the same range as `@sentry/react`, so npm resolves the one copy the SDK requires.
 * Importing it from `@sentry/react` instead would keep it in the entry chunk with the rest of the SDK.
 */
function addReplayWhenIdle() {
  const add = () => {
    import("@sentry/replay").then(
      ({ replayIntegration }) => Sentry.addIntegration(replayIntegration()),
      // A failed chunk load costs only error replays; Sentry itself keeps reporting.
      () => {},
    );
  };
  if (typeof requestIdleCallback === "function") requestIdleCallback(add, { timeout: 5000 });
  else setTimeout(add, 2000);
}
